import { BaseApiUrl } from '../../../configVariables';
import {
    CustomerMessage,
    CustomerMishandledBaggage,
    CustomerRebookedFlight,
    CustomerWithStatuses,
} from '../../../model/customerWithUpdates';
import { PagedCollection } from '../../../model/pagedCollection';
import apiClient from '../../../utils/apiClient';
import {
    CustomerNotificationAction,
    CustomerUpdatesType,
} from './customerUpdates.types';
import {
    CustomerNotificationFilterStatus,
    UnableToNotifyReason,
} from './customerUpdatesSlice';

export type CustomerUpdatesSortableProp =
    | 'FlightScheduledDepartureTime'
    | 'Name'
    | 'FlightIata'
    | 'FlightAirports'
    | 'FlightScheduledArrivalTime'
    | 'SeatNumber'
    | 'FrequentFlyerTier';

export type GetCustomerUpdatesRequest = {
    readonly queryText?: string;
    readonly mishandledBaggageStatus?: CustomerNotificationFilterStatus;
    readonly misconnectedCustomersStatus?: CustomerNotificationFilterStatus;
    readonly messageStatus?: CustomerNotificationFilterStatus;
    readonly cabins?: string[];
    readonly frequentFlyerTiers?: string[];
    readonly flightTeamId?: string;
    readonly updatesType?: CustomerUpdatesType;
    readonly page?: number;
    readonly pageSize?: number;
    readonly orderBy?: CustomerUpdatesSortableProp;
    readonly orderByDescending?: boolean;
};

export type UpdateCustomerNotificationStatusRequest = {
    readonly updatesType: CustomerUpdatesType;
    readonly action: CustomerNotificationAction;
    readonly reason: string | null;
};

export type CustomerUpdatesFilterValues = {
    readonly cabins: readonly string[];
    readonly frequentFlyerTiers: readonly string[];
};

export type CustomerData = {
    readonly firstName: string;
    readonly lastName: string;
    readonly seatNumber: string;
    readonly cabin: string | null;
    readonly frequentFlyerTier: string | null;
    readonly pnr: string | null;
};

export type PostCustomerMessageRequest = {
    readonly flightTeamId: string;
    readonly customers: CustomerData[];
    readonly message: string;
    readonly groundTeamDisplayName: string | null;
};

export type AddCustomerMessageRequest = {
    readonly feedback: string;
};

const baseUrl = BaseApiUrl + '/customerUpdates';

export const fetchCustomerUpdates = async (
    request?: GetCustomerUpdatesRequest
) => {
    return await apiClient.get<PagedCollection<CustomerWithStatuses>>(baseUrl, {
        query: request,
    });
};

export const fetchAvailableFilterValues = async () => {
    return await apiClient.get<CustomerUpdatesFilterValues>(
        `${baseUrl}/filterValues`
    );
};

export const fetchRebookedFlight = async (customerId: number) => {
    return await apiClient.get<CustomerRebookedFlight>(
        `${baseUrl}/${customerId}/rebookedFlight`
    );
};

export const fetchMishandledBaggage = async (customerId: number) => {
    return await apiClient.get<CustomerMishandledBaggage>(
        `${baseUrl}/${customerId}/mishandledBaggage`
    );
};

export const fetchCustomerMessages = async (customerId: number) => {
    return await apiClient.get<CustomerMessage[]>(
        `${baseUrl}/${customerId}/messages`
    );
};

export const postCustomerMessage = async (
    request: PostCustomerMessageRequest
) => {
    const url = `${baseUrl}/messages`;
    await apiClient.post(url, request);
};

export const addCustomerMessageFeedback = async (
    messageId: number,
    request: AddCustomerMessageRequest
) => {
    const url = `${baseUrl}/messages/${messageId}`;
    await apiClient.post(url, request);
};

export const fetchUnableToNotifyReasons = async (
    query?: string | null
): Promise<UnableToNotifyReason[]> => {
    const url = `${baseUrl}/unableToNotifyReasons`;

    const queryStr = query ? `?query=${query.slice(0, 100)}` : '';

    const result = await apiClient.get<UnableToNotifyReason[]>(url + queryStr);
    return result.data;
};

export const upsertUnableToNotifyReason = async (
    id: string | null,
    displayName: string
): Promise<void> => {
    const url = `${BaseApiUrl}/appsettings/unableToNotifyReasons/${id}`;
    await apiClient.post(url, {
        displayName,
    });
};

export const deleteUnableToNotifyReason = async (id: string): Promise<void> => {
    const url = `${BaseApiUrl}/appsettings/unableToNotifyReasons/${id}`;
    await apiClient.delete(url);
};

export const markCustomerUpdateNotificationStatus = async (
    customerId: number,
    request: UpdateCustomerNotificationStatusRequest
) => {
    await apiClient.post(
        `${baseUrl}/${customerId}/notificationStatus`,
        request
    );
};
